import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@songtradr-one/components-frontend";
import {
  FALLBACK_LOCALE,
  Props,
  getLocaleDateConfig,
  localeStringToTimestamp,
  timestampToLocaleString,
} from "./ControlledInput.Parts";

export const ControlledInput = ({
  fieldName,
  fieldValue,
  onFieldChange,
  onFieldBlur,
  label,
  placeholder,
  disabled,
  readOnly,
  hasError,
}: Props) => {
  const locale =
    typeof navigator !== "undefined" ? navigator.language : FALLBACK_LOCALE;
  const localeConfig = useMemo(() => getLocaleDateConfig(locale), [locale]);

  const [inputValue, setInputValue] = useState(() =>
    timestampToLocaleString(fieldValue, locale),
  );
  // Skip the next external-sync effect when fieldValue changed because of
  // our own onChange — otherwise mid-typing input like "01/15/200" would be
  // reformatted away (it parses to null and round-trips to "").
  const skipNextSyncRef = useRef(false);

  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    setInputValue(timestampToLocaleString(fieldValue, locale));
  }, [fieldValue, locale]);

  return (
    <Input
      type="text"
      fullWidth
      label={label}
      name={fieldName}
      value={inputValue}
      placeholder={placeholder || localeConfig.placeholder}
      pattern={localeConfig.pattern}
      inputMode="numeric"
      maxLength={localeConfig.placeholder.length}
      disabled={disabled}
      readOnly={readOnly}
      hasError={hasError}
      onChange={(event) => {
        const next = event.target.value;
        setInputValue(next);
        skipNextSyncRef.current = true;
        onFieldChange(localeStringToTimestamp(next, locale));
      }}
      onBlur={onFieldBlur}
    />
  );
};
